import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Loader2, SkipForward, PhoneOff, Calendar, FileText, MapPin, Mail, X as XIcon, AlertCircle, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";

const OUTCOMES = [
  { key: "no_answer",    label: "Niemand erreicht",    color: "#94a3b8", icon: PhoneOff },
  { key: "no_interest",  label: "Kein Interesse",       color: "#f59e0b", icon: XIcon },
  { key: "callback",     label: "Rückruf vereinbaren",  color: "#60a5fa", icon: Calendar },
  { key: "wrong_number", label: "Falsche Nummer",       color: "#ef4444", icon: AlertCircle },
  { key: "do_not_call",  label: "Sperren (Robinson)",   color: "#7f1d1d", icon: XIcon },
];

export default function TelesalesNextCall() {
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [outcomeNote, setOutcomeNote] = useState("");
  const [callbackAt, setCallbackAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchNext = useCallback(async () => {
    setLoading(true);
    setEntry(null);
    try {
      const { data } = await api.get("/telesales/next-call");
      if (!data || !data.id) {
        setEntry(null);
      } else {
        setEntry(data);
      }
    } catch (e) {
      const msg = e.response?.data?.detail;
      if (e.response?.status === 403) {
        toast.error(msg || "Sie sind keinem Telesales-Team zugeordnet");
      } else {
        toast.error(msg || "Laden fehlgeschlagen");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNext(); }, [fetchNext]);

  const releaseAndNext = async () => {
    if (!entry) return;
    try {
      await api.post(`/telesales/entries/${entry.id}/release`);
      toast.success("Adresse zurückgegeben");
      await fetchNext();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zurückgeben fehlgeschlagen");
    }
  };

  const submitOutcome = async () => {
    if (!entry || !outcome) return;
    if (outcome === "callback" && !callbackAt) {
      toast.error("Bitte Rückruf-Zeitpunkt angeben");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/telesales/entries/${entry.id}/outcome`, {
        outcome,
        note: outcomeNote || null,
        callback_at: outcome === "callback" ? new Date(callbackAt).toISOString() : null,
      });
      toast.success("Outcome erfasst");
      setOutcomeOpen(false);
      setOutcome(null); setOutcomeNote(""); setCallbackAt("");
      await fetchNext();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erfassung fehlgeschlagen");
    } finally { setSubmitting(false); }
  };

  const startCapture = () => {
    if (!entry) return;
    // Build prefill params and navigate to capture
    const params = new URLSearchParams({
      entry_id: entry.id,
      salutation: entry.salutation || "",
      first_name: entry.first_name || "",
      last_name: entry.last_name || "",
      phone: entry.phone || "",
      email: entry.email || "",
      zip: entry.zip || "",
      city: entry.city || "",
      street: entry.street || "",
    });
    navigate(`/telesales?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <AppHeader title="Nächster Anruf" back="/dashboard" />
        <main className="max-w-2xl mx-auto px-4 py-12 flex items-center justify-center">
          <Loader2 className="animate-spin text-zinc-500" size={28} />
        </main>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="min-h-screen bg-[#09090B]" data-testid="telesales-no-calls">
        <AppHeader title="Nächster Anruf" back="/dashboard" />
        <main className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center">
            <div className="inline-flex h-16 w-16 rounded-full bg-zinc-800 items-center justify-center mb-4">
              <PhoneOff className="text-zinc-500" size={28} />
            </div>
            <h2 className="font-display text-xl font-bold mb-2">Keine offenen Adressen</h2>
            <p className="text-zinc-400 text-sm mb-6">
              Aktuell sind keine Adressen verfügbar. Bitte später erneut prüfen oder den
              Direktor um eine neue Liste bitten.
            </p>
            <button
              onClick={fetchNext}
              data-testid="next-call-refresh"
              className="h-11 px-5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 inline-flex items-center gap-2"
            >
              <RotateCcw size={16} /> Erneut prüfen
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="telesales-next-call">
      <AppHeader title="Nächster Anruf" back="/dashboard" />
      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
        {/* Pick reason badge */}
        <div className="flex items-center gap-2 text-[12px] uppercase tracking-widest font-bold">
          {entry._pick_reason === "callback" && <span className="px-2 py-0.5 rounded bg-blue-500/15 text-blue-300">📅 Rückruf</span>}
          {entry._pick_reason === "direct" && <span className="px-2 py-0.5 rounded bg-purple-500/15 text-purple-300">⌧ Direkt zugewiesen</span>}
          {entry._pick_reason === "pool" && <span className="px-2 py-0.5 rounded bg-zinc-700 text-zinc-300">⚡ Pool-Pick</span>}
        </div>

        {/* Contact card */}
        <section className="bg-[#18181B] border border-[#00E575]/30 rounded-2xl p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[12px] uppercase tracking-widest text-zinc-300 mb-1">Anrufen</div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold" data-testid="next-call-name">
                {entry.salutation ? `${entry.salutation} ` : ""}{entry.first_name} {entry.last_name}
              </h1>
            </div>
          </div>
          <div className="space-y-3">
            {entry.phone && (
              <a
                href={`tel:${entry.phone}`}
                data-testid="next-call-phone"
                className="flex items-center gap-3 p-4 rounded-xl bg-[#00E575]/10 hover:bg-[#00E575]/15 border border-[#00E575]/40 transition-colors"
              >
                <Phone className="text-[#00E575]" size={22} />
                <div>
                  <div className="text-[12px] uppercase tracking-widest text-[#00E575]/80">Telefon — antippen zum Wählen</div>
                  <div className="font-mono text-lg text-zinc-100">{entry.phone}</div>
                </div>
              </a>
            )}
            {entry.email && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[#09090B] border border-zinc-800">
                <Mail className="text-zinc-500" size={18} />
                <div className="font-mono text-[13px] text-zinc-300">{entry.email}</div>
              </div>
            )}
            {(entry.street || entry.zip || entry.city) && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-[#09090B] border border-zinc-800">
                <MapPin className="text-zinc-500 mt-0.5" size={18} />
                <div className="text-[13px] text-zinc-300">
                  {entry.street && <div>{entry.street}</div>}
                  {(entry.zip || entry.city) && <div>{entry.zip} {entry.city}</div>}
                </div>
              </div>
            )}
            {entry.note && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <FileText className="text-amber-400 mt-0.5" size={18} />
                <div className="text-[13px] text-amber-200">{entry.note}</div>
              </div>
            )}
          </div>
        </section>

        {/* Action buttons */}
        <div className="grid grid-cols-1 gap-3">
          <button
            onClick={startCapture}
            data-testid="next-call-start-capture"
            className="w-full h-14 rounded-xl bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 text-[15px]"
          >
            <FileText size={18} /> Lead erfassen (Interesse positiv)
          </button>
          <button
            onClick={() => setOutcomeOpen(true)}
            data-testid="next-call-outcome"
            className="w-full h-12 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 inline-flex items-center justify-center gap-2"
          >
            <PhoneOff size={16} /> Outcome ohne Lead (kein Interesse, Rückruf, …)
          </button>
          <button
            onClick={releaseAndNext}
            data-testid="next-call-skip"
            className="w-full h-11 rounded-xl bg-transparent hover:bg-zinc-900 border border-zinc-800 text-zinc-400 inline-flex items-center justify-center gap-2 text-[13px]"
          >
            <SkipForward size={14} /> Überspringen
          </button>
        </div>

        {/* Outcome modal */}
        {outcomeOpen && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3" data-testid="outcome-modal">
            <div className="w-full max-w-md bg-[#18181B] border border-zinc-800 rounded-2xl">
              <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
                <h3 className="font-bold">Outcome erfassen</h3>
                <button onClick={() => setOutcomeOpen(false)} className="p-1 hover:bg-zinc-800 rounded">
                  <XIcon size={16} />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <div className="grid grid-cols-1 gap-2">
                  {OUTCOMES.map((o) => {
                    const Icon = o.icon;
                    const selected = outcome === o.key;
                    return (
                      <button
                        key={o.key}
                        onClick={() => setOutcome(o.key)}
                        data-testid={`outcome-${o.key}`}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          selected
                            ? "border-[#00E575]/60 bg-[#00E575]/10"
                            : "border-zinc-800 bg-[#09090B] hover:border-zinc-600"
                        }`}
                        style={selected ? {} : { color: o.color }}
                      >
                        <Icon size={18} style={{ color: o.color }} />
                        <span className={`text-[13px] font-medium ${selected ? "text-zinc-100" : "text-zinc-300"}`}>
                          {o.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {outcome === "callback" && (
                  <div>
                    <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Rückruf am *</label>
                    <input
                      type="datetime-local"
                      value={callbackAt}
                      onChange={(e) => setCallbackAt(e.target.value)}
                      data-testid="outcome-callback-at"
                      className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Notiz (optional)</label>
                  <textarea
                    value={outcomeNote}
                    onChange={(e) => setOutcomeNote(e.target.value)}
                    data-testid="outcome-note"
                    rows={2}
                    className="w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 focus:border-[#00E575] outline-none text-[13px]"
                  />
                </div>
                <button
                  onClick={submitOutcome}
                  disabled={!outcome || submitting}
                  data-testid="outcome-submit"
                  className="w-full h-11 rounded-xl bg-[#00E575] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#00C964] disabled:bg-zinc-800 disabled:text-zinc-500"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : "Speichern & Nächster"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
